import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { compose } from 'redux';
import { Snackbar } from '@mui/material';
import { Alert } from '@mui/material';
import Form from '../../../components/general/form';
import Loader from '../../../components/Loader';
import { INSURANCE_CLAIM_FIELDS, populateInsuranceOptions, populateOrganizationOptions } from './fields';
import { InsuranceClaimsApiService } from './services/insuranceClaimsApi';
import { useSnackBarState } from '../../../hooks';
import { 
    InsuranceClaimFormProps, 
    InsuranceClaimFormData,
    InsuranceClaimApiPayload,
    InsuranceClaim,
    ApiResult,
    Insurance, 
    InsuranceClaimStatus
} from './types';
import { v4 as uuidv4 } from 'uuid';
import { useInsurancesList } from '../../../hooks/usePatientInsurance';
import { useOrganizations } from '../../../hooks/useOrganizations';

// Define the combined props interface
interface InsuranceClaimFormComponentProps extends 
    InsuranceClaimFormProps, 
    InjectedFormProps<InsuranceClaimFormData> {
    translate: (key: string) => string;
}

/**
 * Insurance Claim Form Component
 * 
 * This component handles the creation and editing of insurance claims
 * using the existing Sherwood-Fields form system with Redux Form.
 */
const InsuranceClaimForm: React.FC<InsuranceClaimFormComponentProps> = (props) => {
    const [snackbar, setSnackbar] = useSnackBarState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Get organizations from backend
    const [organizations, loadingOrganizations, organizationsError] = useOrganizations(props.uuidInvestigation);
    
    // Get insurances from currentInvestigation
    const [insurances, loadingInsurances] = useInsurancesList();
    
    // Use mock API service in Storybook if available, otherwise use real service
    const apiService = (window as any).mockApiService || new InsuranceClaimsApiService();

    /**
     * Populate the organization autocomplete with organizations from backend
     */
    const populateOrganizationDropdown = () => {
        if (organizations.length > 0) {
            populateOrganizationOptions(organizations);
        }
    };

    // Populate organization options when organizations are fetched
    useEffect(() => {
        populateOrganizationDropdown();
    }, [organizations]);

    /**
     * Populate the insurance dropdown with available options
     */
    const populateInsuranceDropdown = () => {
        if (props.availableInsurances && props.availableInsurances.length > 0) {
            populateInsuranceOptions(props.availableInsurances);
        }
    };

    // Populate insurance options when component mounts or availableInsurances change
    useEffect(() => {
        populateInsuranceDropdown();
    }, [props.availableInsurances]);

    /**
     * Handle form submission
     * @param values - Form values from redux-form
     * @param buttonSubmitted - Which button was clicked (optional)
     */
    const handleFormSubmit = async (values: InsuranceClaimFormData, buttonSubmitted?: string) => {
        try {
            let result: ApiResult<InsuranceClaim>;
            setIsSubmitting(true);
            // Generate idempotency key
            const idempotencyKey = uuidv4();
            
            // Transform form data: extract organizationId from organizationName field (which contains the ID as string)
            // and exclude organizationName from submission since API only needs organizationId
            const organizationId = values.organizationName ? parseInt(values.organizationName, 10) : undefined;
            const { organizationName, ...formDataWithoutOrgName } = values;
            
            const submissionData: InsuranceClaimApiPayload = {
                ...formDataWithoutOrgName,
                organizationId: organizationId
            };
            
            // Validate that approvedAmount is required if status is APPROVED
            if (submissionData.status === InsuranceClaimStatus.APPROVED && !submissionData.approvedAmount) {
                setSnackbar({ 
                    show: true, 
                    severity: "error", 
                    message: "hospital.billing.insurance_claims.validation.approved_amount_required" 
                });
                setIsSubmitting(false);
                return;
            }

            // Clear approvedAmount from submission if status is not APPROVED
            if (submissionData.status !== InsuranceClaimStatus.APPROVED) {
                delete submissionData.approvedAmount;
            }
            
            if (props.mode === 'create') {
                result = await apiService.createClaim(
                    props.uuidInvestigation,
                    props.uuidBill,
                    submissionData,
                    idempotencyKey
                );
                
                // Accept both 200 and 201 as successful responses
                if (result.status === 200 || result.status === 201) {
                    // Show success message
                    setSnackbar({ 
                        show: true, 
                        severity: "success", 
                        message: "pages.hospital.billing.insurance_claims.messages.create_success" 
                    });
                    // Delay closing the modal to allow snackbar to show
                    if (typeof props.onSuccess === 'function') {
                        setTimeout(() => {
                            props.onSuccess && props.onSuccess(result.data);
                        }, 1000); // 1 second delay
                    }
                }
            } else {
                // Update existing claim
                if (!props.initialData?.uuid) {
                    throw new Error('Cannot update claim: missing claim UUID');
                }
                
                result = await apiService.updateClaim(
                    props.uuidInvestigation,
                    props.initialData.uuid,
                    submissionData,
                    idempotencyKey
                );
                
                // Accept both 200 and 201 as successful responses
                if (result.status === 200 || result.status === 201) {
                    setSnackbar({ 
                        show: true, 
                        severity: "success", 
                        message: "pages.hospital.billing.insurance_claims.messages.update_success" 
                    });
                    // Delay closing the modal to allow snackbar to show
                    if (typeof props.onSuccess === 'function') {
                        setTimeout(() => {
                            props.onSuccess && props.onSuccess(result.data);
                        }, 1000); // 1 second delay
                    }
                } else {
                    throw new Error(`Unexpected response status: ${result.status}`);
                }
            }

        } catch (error) {
            console.error('Form submission error:', error);
            const errorCode = (error as any)?.details?.errorCode;
            switch(errorCode){
                case "INSURANCE_CLAIM_ALREADY_PAID":
                    setSnackbar({ 
                        show: true, 
                        severity: "error", 
                        message: "pages.hospital.billing.insurance_claims.messages.already_paid" 
                    });
                    break;
                case "INVALID_COVERAGE_PERCENTAGE":
                    setSnackbar({ 
                        show: true, 
                        severity: "error", 
                        message: "pages.hospital.billing.insurance_claims.messages.invalid_coverage_percentage" 
                    });
                    break;
                default:
                    setSnackbar({ 
                        show: true, 
                        severity: "error", 
                        message: "pages.hospital.billing.insurance_claims.messages.submit_error" 
                    });
                    break;
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Handle form cancellation
     */
    const handleFormCancel = () => {
        if (props.onCancel) {
            props.onCancel();
        }
    };

    /**
     * Get the submit button text based on mode
     */
    const getSubmitButtonText = (): string => {
        return props.mode === 'create' 
            ? 'pages.hospital.billing.insurance_claims.buttons.create'
            : 'pages.hospital.billing.insurance_claims.buttons.update';
    };

    /**
     * Get the cancel button text
     */
    const getCancelButtonText = (): string => {
        return 'pages.hospital.billing.insurance_claims.buttons.cancel';
    };

    /**
     * Handle snackbar close
     */
    const handleSnackbarClose = () => {
        setSnackbar({ show: false, severity: "info", message: "" });
    };

    const {
        uuidInvestigation,
        uuidBill,
        mode,
        initialData,
        availableInsurances,
        handleSubmit,
        submitting,
        pristine,
        valid,
        translate
    } = props;

    // Show loading state while organizations are being fetched
    if (loadingOrganizations) {
        return <Loader infoString={translate('pages.hospital.billing.insurance_claims.loading_organizations') || 'Loading organizations...'} />;
    }

    // Show error state if organizations failed to load
    if (organizationsError) {
        return (
            <div data-testid="insurance-claim-form-error">
                <Alert severity="error">
                    {translate('pages.hospital.billing.insurance_claims.error_loading_organizations') || 'Failed to load organizations. Please try again.'}
                </Alert>
            </div>
        );
    }

    // Transform initialData for edit mode: convert organizationId to organizationName (as string ID)
    const transformedInitialData = initialData ? {
        ...initialData,
        organizationName: initialData.organizationId 
            ? initialData.organizationId.toString() 
            : initialData.organizationName || ''
    } : initialData;

    return (
        <div data-testid="insurance-claim-form">
            <Form
                fields={INSURANCE_CLAIM_FIELDS}
                callBackForm={handleFormSubmit}
                creating={mode === 'create'}
                initialData={transformedInitialData}
                inSubmission={isSubmitting}
                submitText={getSubmitButtonText()}
                cancelText={getCancelButtonText()}
                closeCallBack={handleFormCancel}
                fullWidth={true}
                uuidInvestigation={uuidInvestigation}
                // Additional props that might be needed by the Form component
                dataTestid={`insurance-claim-form-${mode}`}
            />
            
            {/* Snackbar for user feedback */}
            <Snackbar
                open={snackbar.show}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleSnackbarClose} 
                    severity={snackbar.severity as any}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message ? translate(snackbar.message) : ''}
                </Alert>
            </Snackbar>
        </div>
    );
};

// Define the form configuration for redux-form
const formConfig = {
    form: 'insuranceClaimForm', // Unique identifier for this form
    // You can add form-level validation here if needed
    // validate: validateInsuranceClaimForm,
    enableReinitialize: true, // Allow form to reinitialize when initialValues change
    destroyOnUnmount: false, // Keep form data when component unmounts
};

// Compose the HOCs
export default compose<React.ComponentType<InsuranceClaimFormProps>>(
    connect(), // Connect to Redux store
    reduxForm(formConfig), // Add redux-form functionality
    withLocalize // Add localization support
)(InsuranceClaimForm); 